import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api } from "../lib/api";
import { Card, Badge, Empty, Btn, Modal } from "../components/UI";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  UserX, CalendarDays, FileWarning, ShieldCheck, AlertTriangle, ListChecks, PenLine, Inbox,
  CalendarClock, Users, CheckSquare, Activity, Brain, Zap, ChevronRight, CalendarX2, UsersRound, UserCog,
  SlidersHorizontal, GripVertical, FlaskConical, Syringe, Pill, ClipboardList, FileClock,
  Stethoscope, HeartPulse, Apple, ScrollText, FileSignature, LogOut, Siren, ClipboardX, RotateCcw,
  UserPlus, Sparkles, Maximize2, Minimize2,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell, PieChart, Pie, Legend } from "recharts";
import { MiniCalendar } from "../components/MiniCalendar";
import { InfoTip } from "../components/InfoTip";
import { QuickClientModal } from "../components/QuickClientModal";
import { useDensity } from "../context/DensityContext";

const COCOA_COLORS = ["#4E342E", "#8D6E63", "#A1887F", "#FFB300", "#6D4C41", "#BCAAA4", "#E65100", "#2E7D32"];

function CountUp({ value }) {
  const [n, setN] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const target = Number(value) || 0;
    const t0 = performance.now();
    const dur = 900;
    cancelAnimationFrame(ref.current);
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value]);
  return <>{n}</>;
}

const SEV_STYLE = {
  critical: "border-l-4 border-[#C62828] bg-[#FFEBEE]/60",
  warning: "border-l-4 border-[#E65100] bg-[#FFF3E0]/50",
  info: "border-l-4 border-[#1565C0] bg-[#E3F2FD]/50",
  ok: "border-l-4 border-[#2E7D32] bg-[#E8F5E9]/60",
};

const initials = (n) => (n || "").split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const Row = ({ left, right, sub, onClick, testId, av }) => (
  <div data-testid={testId} onClick={onClick} role={onClick ? "button" : undefined} tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    className={`flex items-center justify-between border-b border-[#EFEBE0] last:border-0 py-2 ${onClick ? "cursor-pointer rounded-lg px-1.5 -mx-1.5 hover:bg-[#F5F2EB] transition-colors" : ""}`}>
    <div className="flex items-center gap-2.5 min-w-0">
      {av && (av.photo
        ? <img src={av.photo} alt="" className="h-8 w-8 rounded-full object-cover shrink-0 ring-1 ring-[#EADDD0]" />
        : <div className="h-8 w-8 rounded-full bg-[#EADDD0] text-[#4E342E] text-[10px] font-bold flex items-center justify-center shrink-0">{initials(av.name)}</div>)}
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-[#3E2723]">{left}</div>
        {sub && <div className="text-xs text-[#84675B]">{sub}</div>}
      </div>
    </div>
    <div className="ml-2 shrink-0 text-xs text-[#6D4C41]">{right}</div>
  </div>
);

function ChartTip({ active, payload, render }) {
  if (!active || !payload?.length) return null;
  return <div className="rounded-xl border border-[#EFEBE0] bg-white/95 backdrop-blur px-3.5 py-2.5 shadow-lg text-xs">{render(payload[0].payload)}</div>;
}

const wRows = (w, key, mapFn, emptyText, ctx) => {
  const data = w?.[key];
  if (!data) return <div className="py-6 text-center text-xs text-[#84675B]">Loading…</div>;
  if (data.rows.length === 0) return <Empty text={emptyText} />;
  return data.rows.slice(0, ctx?.all ? 1000 : 5).map(mapFn);
};

const wCount = (w, key) => w?.[key]?.count;

/* Registry: every dashboard widget. render receives {d, w, ctx} */
const REGISTRY = [
  { key: "stat_cards", title: "Stat Cards", icon: Users, group: "Core", span: "full" },
  { key: "action_center", title: "Action Center", icon: Brain, group: "Core", span: "full" },
  { key: "today_appointments", title: "Today's Appointments", icon: CalendarDays, group: "Scheduling", link: "/scheduler",
    count: (d) => d.todays_appointments.length,
    render: (d, w, ctx) => d.todays_appointments.length === 0 ? <Empty text="No appointments today" /> :
      d.todays_appointments.slice(0, ctx?.all ? 1000 : 5).map((a) => <Row key={a.id} av={{ name: a.client_name, photo: ctx.photos[a.client_id] }} left={a.client_name} sub={a.type} right={a.start_time} onClick={() => ctx.toClient(a.client_id)} />) },
  { key: "missed_appointments", title: "Missed Appointments", icon: CalendarX2, group: "Scheduling", link: "/scheduler",
    count: (d) => d.missed_appointments.length,
    render: (d, w, ctx) => d.missed_appointments.length === 0 ? <Empty text="No missed appointments" /> :
      d.missed_appointments.slice(0, ctx?.all ? 1000 : 5).map((m) => <Row key={m.id} av={{ name: m.client_name, photo: ctx.photos[m.client_id] }} left={m.client_name} sub={m.type} right={dayjs(m.date).format("MM/DD")} onClick={() => ctx.toClient(m.client_id)} />) },
  { key: "appointment_requests", title: "Appointment Requests", icon: CalendarClock, group: "Scheduling", link: "/scheduler",
    count: (d) => d.appointment_requests.length,
    render: (d, w, ctx) => d.appointment_requests.length === 0 ? <Empty text="No pending requests" /> :
      d.appointment_requests.slice(0, ctx?.all ? 1000 : 5).map((r) => (
        <div key={r.id} className="border-b border-[#EFEBE0] last:border-0 py-2">
          <div className="text-sm font-medium">{r.client_name}</div>
          <div className="text-xs text-[#84675B] mb-1.5">{r.type} · {r.requested_date} {r.requested_time}</div>
          <div className="flex gap-2">
            <Btn data-testid={`approve-request-${r.id}`} className="px-3 py-1 text-xs" onClick={() => ctx.actRequest(r.id, "approved")}>Approve</Btn>
            <Btn data-testid={`deny-request-${r.id}`} variant="outline" className="px-3 py-1 text-xs" onClick={() => ctx.actRequest(r.id, "denied")}>Deny</Btn>
          </div>
        </div>
      )) },
  { key: "absent_7d", title: "Absent > 7 Days", icon: UserX, group: "Clinical", link: "/clients",
    count: (d) => d.absent_clients.length,
    render: (d, w, ctx) => d.absent_clients.length === 0 ? <Empty text="Everyone seen recently" /> :
      d.absent_clients.slice(0, ctx?.all ? 1000 : 5).map((c) => <Row key={c.id} av={{ name: `${c.first_name} ${c.last_name}`, photo: c.photo || ctx.photos[c.id] }} left={`${c.first_name} ${c.last_name}`} sub={c.mrn} right={`Last: ${(c.last_seen || "").slice(0, 10)}`} onClick={() => ctx.toClient(c.id)} />) },
  { key: "high_risk", title: "High-Risk Alerts (C-SSRS)", icon: AlertTriangle, group: "Clinical", link: "/outcome-measures",
    count: (d) => d.high_risk_alerts.length,
    render: (d, w, ctx) => d.high_risk_alerts.length === 0 ? <Empty text="No high-risk screens in 90 days" /> :
      d.high_risk_alerts.slice(0, ctx?.all ? 1000 : 5).map((h) => <Row key={h.id} av={{ name: h.client_name, photo: ctx.photos[h.client_id] }} left={h.client_name} sub={h.severity} right={dayjs(h.date).format("MM/DD")} onClick={() => ctx.toClient(h.client_id)} />) },
  { key: "treatment_plans_due", title: "Treatment Plans Due", icon: FileWarning, group: "Clinical", link: "/clinical/treatment-plans",
    count: (d) => d.treatment_plans_due.length,
    render: (d, w, ctx) => d.treatment_plans_due.length === 0 ? <Empty text="All plans current" /> :
      d.treatment_plans_due.slice(0, ctx?.all ? 1000 : 5).map((p) => <Row key={p.id} av={{ name: p.client_name, photo: ctx.photos[p.client_id] }} left={p.client_name} right={<Badge value={p.status} />} sub={`Due ${p.due_date}`} onClick={() => ctx.toClient(p.client_id)} />) },
  { key: "behaviors", title: "Recent Behavior Incidents", icon: Activity, group: "Clinical",
    count: (d) => d.recent_behaviors.length,
    render: (d, w, ctx) => d.recent_behaviors.length === 0 ? <Empty text="No behavior entries" /> :
      d.recent_behaviors.slice(0, ctx?.all ? 1000 : 5).map((b) => <Row key={b.id} av={{ name: b.client_name, photo: ctx.photos[b.client_id] }} left={b.client_name} sub={`${b.category} · severity ${b.severity}`} right={dayjs(b.date).format("MM/DD")} onClick={() => ctx.toClient(b.client_id)} />) },
  { key: "allergies", title: "Allergies by Type", icon: AlertTriangle, group: "Clinical", link: "/reports", noShowAll: true,
    count: (d, w) => wCount(w, "allergies"),
    render: (d, w) => {
      const rows = w?.allergies?.rows || [];
      if (!w) return <div className="py-6 text-center text-xs text-[#84675B]">Loading…</div>;
      if (rows.length === 0) return <Empty text="No allergies on file" />;
      return (
        <ResponsiveContainer width="100%" height={Math.min(230, Math.max(130, rows.length * 26))}>
          <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 24, left: 0, bottom: 0 }}>
            <XAxis type="number" allowDecimals={false} hide />
            <YAxis type="category" dataKey="allergen" width={92} axisLine={false} tickLine={false} tick={{ fontSize: 11.5, fill: "#4E342E" }} />
            <Tooltip cursor={{ fill: "#F5F2EB" }} content={<ChartTip render={(r) => (
              <div className="space-y-0.5">
                <div className="font-bold text-[#3E2723]">{r.allergen} <span className="font-normal text-[#84675B]">· {r.type || "Allergy"}</span></div>
                <div className="text-[#6D4C41]">{r.clients} client{r.clients === 1 ? "" : "s"} ({r.pct}%)</div>
                {r.severity && <div className="text-[#7B6157]">Severity: {r.severity}{r.reaction ? ` · ${r.reaction}` : ""}</div>}
                {r.names?.length > 0 && <div className="text-[#84675B]">{r.names.filter(Boolean).join(", ")}</div>}
              </div>
            )} />} />
            <Bar dataKey="clients" radius={[0, 6, 6, 0]} barSize={12} label={{ position: "right", fontSize: 10, fill: "#8D6E63" }}>
              {rows.map((r, i) => <Cell key={i} fill={COCOA_COLORS[i % COCOA_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    } },
  { key: "appointments_week", title: "Appointments This Week", icon: CalendarDays, group: "Scheduling", link: "/scheduler", noShowAll: true,
    count: (d, w) => wCount(w, "appointments_week"),
    render: (d, w) => {
      const rows = w?.appointments_week?.rows || [];
      if (!w) return <div className="py-6 text-center text-xs text-[#84675B]">Loading…</div>;
      return (
        <ResponsiveContainer width="100%" height={170}>
          <BarChart data={rows} margin={{ top: 5, right: 4, left: -28, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" vertical={false} />
            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11.5, fill: "#6D4C41" }} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 10.5, fill: "#8D6E63" }} />
            <Tooltip cursor={{ fill: "#F5F2EB" }} content={<ChartTip render={(r) => (
              <div className="space-y-0.5">
                <div className="font-bold text-[#3E2723]">{r.day} {dayjs(r.date).format("MM/DD")} - {r.count} appointment{r.count === 1 ? "" : "s"}</div>
                {(r.appts || []).map((a, i) => <div key={i} className="text-[#6D4C41]">{a.start_time} · {a.client_name}{a.type ? ` (${a.type})` : ""}</div>)}
              </div>
            )} />} />
            <Bar dataKey="count" radius={[6, 6, 0, 0]} barSize={22}>
              {rows.map((r, i) => <Cell key={i} fill={r.date === dayjs().format("YYYY-MM-DD") ? "#FFB300" : "#8D6E63"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      );
    } },
  { key: "mini_calendar", title: "Day Schedule", icon: CalendarDays, group: "Scheduling", link: "/scheduler", noShowAll: true, span: "full",
    render: (d, w, ctx) => <MiniCalendar navigate={ctx.navigate} /> },
  { key: "client_status_dist", title: "Client Status Distribution", icon: Users, group: "Operations", link: "/clients", noShowAll: true,
    count: (d, w) => wCount(w, "client_status_dist"),
    render: (d, w) => {
      const rows = w?.client_status_dist?.rows || [];
      if (!w) return <div className="py-6 text-center text-xs text-[#84675B]">Loading…</div>;
      if (rows.length === 0) return <Empty text="No clients yet" />;
      return (
        <ResponsiveContainer width="100%" height={185}>
          <PieChart>
            <Pie data={rows} dataKey="count" nameKey="status" innerRadius={48} outerRadius={75} paddingAngle={3} strokeWidth={0}>
              {rows.map((r, i) => <Cell key={i} fill={COCOA_COLORS[i % COCOA_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n) => [`${v} clients`, n]} contentStyle={{ borderRadius: 12, border: "1px solid #EFEBE0" }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
          </PieChart>
        </ResponsiveContainer>
      );
    } },
  { key: "unsigned_documents", title: "Unsigned Documents", icon: FileSignature, group: "Clinical", link: "/clinical/progress-notes",
    count: (d, w) => wCount(w, "unsigned_documents"),
    render: (d, w, ctx) => wRows(w, "unsigned_documents", (r, i) => <Row key={i} av={{ name: r.client_name, photo: ctx.photos[r.client_id] }} left={r.client_name} sub={r.title} right={r.date} onClick={() => ctx.toClient(r.client_id)} />, "All documents signed", ctx) },
  { key: "pending_cosign", title: "Pending Co-Signatures", icon: PenLine, group: "Clinical", link: "/clinical/progress-notes",
    count: (d, w) => wCount(w, "pending_cosign"),
    render: (d, w, ctx) => wRows(w, "pending_cosign", (r, i) => <Row key={i} av={{ name: r.client_name, photo: ctx.photos[r.client_id] }} left={r.client_name} sub={r.title} right={r.date} onClick={() => ctx.navigate(`/clinical/progress-notes/${r.note_id}`)} />, "No notes awaiting co-signature", ctx) },
  { key: "service_not_provided_7d", title: "Service Not Provided > 7 Days", icon: ClipboardX, group: "Clinical",
    count: (d, w) => wCount(w, "service_not_provided_7d"),
    render: (d, w, ctx) => wRows(w, "service_not_provided_7d", (r, i) => <Row key={i} left={r.client_name} sub={r.title} right={r.date} />, "No service gaps recorded", ctx) },
  { key: "critical_incidents", title: "Critical Incidents", icon: Siren, group: "Clinical",
    count: (d, w) => wCount(w, "critical_incidents"),
    render: (d, w) => wRows(w, "critical_incidents", (r, i) => <Row key={i} left={r.client_name} sub={r.title} right={r.date} />, "No incidents reported") },
  { key: "labs", title: "Labs", icon: FlaskConical, group: "Clinical",
    count: (d, w) => wCount(w, "labs"),
    render: (d, w, ctx) => wRows(w, "labs", (r, i) => <Row key={i} left={r.client_name} sub={r.title} right={r.date} />, "No lab results on file", ctx) },
  { key: "client_tasks", title: "Client Tasks", icon: ListChecks, group: "Operations", link: "/tasks",
    count: (d) => d.client_tasks.length,
    render: (d, w, ctx) => d.client_tasks.length === 0 ? <Empty text="No open tasks" /> :
      d.client_tasks.slice(0, ctx?.all ? 1000 : 5).map((t) => <Row key={t.id} left={t.title} sub={t.client_name} right={<Badge value={t.priority} />} onClick={() => ctx.navigate("/tasks")} />) },
  { key: "unsigned_tasks", title: "Unsigned Tasks", icon: PenLine, group: "Operations", link: "/tasks",
    count: (d) => d.unsigned_tasks.length,
    render: (d, w, ctx) => d.unsigned_tasks.length === 0 ? <Empty text="Nothing to sign" /> :
      d.unsigned_tasks.slice(0, ctx?.all ? 1000 : 4).map((t) => (
        <div key={t.id} className="flex items-center justify-between border-b border-[#EFEBE0] last:border-0 py-2">
          <div className="min-w-0 text-sm font-medium truncate">{t.title}</div>
          <Btn variant="outline" data-testid={`sign-task-${t.id}`} className="px-3 py-1 text-xs" onClick={async () => { await api.put(`/tasks/${t.id}/sign`); toast.success("Task signed"); ctx.load(); }}>Sign</Btn>
        </div>
      )) },
  { key: "incoming_referrals", title: "Incoming Referrals", icon: Inbox, group: "Operations", link: "/engagement/referrals",
    count: (d) => d.incoming_referrals.length,
    render: (d, w, ctx) => d.incoming_referrals.length === 0 ? <Empty text="No new referrals" /> :
      d.incoming_referrals.slice(0, ctx?.all ? 1000 : 5).map((r) => <Row key={r.id} av={{ name: r.client_name }} left={r.client_name} sub={r.referred_by} right={r.date} onClick={() => ctx.navigate("/engagement/referrals")} />) },
  { key: "authorizations", title: "Authorizations", icon: ShieldCheck, group: "Operations",
    count: (d) => d.authorizations.length,
    render: (d, w, ctx) => d.authorizations.length === 0 ? <Empty text="No authorizations" /> :
      d.authorizations.slice(0, ctx?.all ? 1000 : 5).map((a) => <Row key={a.id} av={{ name: a.client_name, photo: ctx.photos[a.client_id] }} left={a.client_name} sub={`${a.company || ""} · ${a.sessions_used}/${a.sessions_approved} used`} right={<Badge value={a.status} />} onClick={() => ctx.toClient(a.client_id)} />) },
  { key: "financial_summary", title: "Financial Summary", icon: CheckSquare, group: "Operations", link: "/revenue-cycle", span: 2,
    render: (d) => (
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={d.financial_summary} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8D6E63" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#8D6E63" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
          <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#6D4C41" }} />
          <YAxis tick={{ fontSize: 12, fill: "#6D4C41" }} tickFormatter={(v) => `$${v / 1000}k`} />
          <Tooltip formatter={(v) => `$${v.toLocaleString()}`} contentStyle={{ borderRadius: 12, border: "1px solid #EFEBE0" }} />
          <Area type="monotone" dataKey="revenue" stroke="#4E342E" strokeWidth={2} fill="url(#rev)" name="Billed" />
          <Area type="monotone" dataKey="collected" stroke="#8D6E63" strokeWidth={2} fillOpacity={0} name="Collected" />
        </AreaChart>
      </ResponsiveContainer>
    ) },
  { key: "medication_consents", title: "Medication Consents", icon: FileSignature, group: "Medications",
    count: (d, w) => wCount(w, "medication_consents"),
    render: (d, w, ctx) => wRows(w, "medication_consents", (r, i) => <Row key={i} av={{ name: r.client_name }} left={r.client_name} sub={r.title} right={<Badge value={r.status.toLowerCase()} />} />, "No consents on file", ctx) },
  { key: "medications_refill", title: "Medications to Refill", icon: Pill, group: "Medications",
    count: (d, w) => wCount(w, "medications_refill"),
    render: (d, w, ctx) => wRows(w, "medications_refill", (r, i) => <Row key={i} av={{ name: r.client_name }} left={r.client_name} sub={`${r.name} ${r.dose}`} right={r.end_date} />, "No refills due in 30 days", ctx) },
  { key: "medication_requests", title: "Medication Requests", icon: ClipboardList, group: "Medications",
    count: (d, w) => wCount(w, "medication_requests"),
    render: (d, w, ctx) => wRows(w, "medication_requests", (r, i) => <Row key={i} left={r.client_name} sub={r.title} right={r.date} />, "No medication requests", ctx) },
  { key: "missed_injections", title: "Missed Injections", icon: Syringe, group: "Medications",
    count: (d, w) => wCount(w, "missed_injections"),
    render: (d, w, ctx) => wRows(w, "missed_injections", (r, i) => <Row key={i} left={r.client_name} sub={r.title} right={r.date} />, "No missed injections", ctx) },
  ...[
    ["expiring_treatment_plans", "Expiring Treatment Plans", FileClock],
    ["expiring_diagnosis", "Expiring Diagnosis", Stethoscope],
    ["expiring_comprehensive", "Expiring Comprehensive Assessments", ClipboardList],
    ["expiring_substance_abuse", "Expiring Substance Abuse Assessments", FlaskConical],
    ["expiring_nursing", "Expiring Nursing Assessments", HeartPulse],
    ["expiring_aims", "Expiring AIMS", Activity],
    ["expiring_nutritional", "Expiring Nutritional Assessments", Apple],
    ["expiring_risk", "Expiring Risk Assessments", AlertTriangle],
    ["expiring_crisis_plans", "Expiring Crisis Plans", Siren],
    ["expiring_discharge_plans", "Expiring Transition/Discharge Plans", LogOut],
    ["expiring_annual_consents", "Expiring Annual Consent Forms", ScrollText],
    ["expiring_orders", "Expiring Orders", FileWarning],
  ].map(([key, title, icon]) => ({
    key, title, icon, group: "Expiring & Renewals",
    count: (d, w) => wCount(w, key),
    render: (d, w, ctx) => wRows(w, key, (r, i) => <Row key={i} av={{ name: r.client_name }} left={r.client_name} sub={r.title} right={r.date || <Badge value={(r.status || "").toLowerCase().replace(" ", "_")} />} />, "Nothing expiring", ctx),
  })),
];

const GROUP_ORDER = ["Core", "Scheduling", "Clinical", "Operations", "Medications", "Expiring & Renewals"];
const DEFAULT_LAYOUT = ["stat_cards", "action_center", "mini_calendar", "today_appointments", "absent_7d", "treatment_plans_due",
  "authorizations", "client_tasks", "unsigned_documents", "missed_appointments", "incoming_referrals",
  "appointments_week", "allergies", "client_status_dist"];
const ROLE_DEFAULTS = {
  billing: ["stat_cards", "action_center", "authorizations", "financial_summary", "client_tasks", "missed_appointments", "expiring_annual_consents", "incoming_referrals", "client_status_dist"],
  front_desk: ["stat_cards", "action_center", "mini_calendar", "today_appointments", "appointments_week", "appointment_requests", "missed_appointments", "incoming_referrals", "client_tasks", "absent_7d"],
  clinician: ["stat_cards", "action_center", "mini_calendar", "today_appointments", "absent_7d", "treatment_plans_due", "high_risk", "unsigned_documents", "pending_cosign", "client_tasks", "missed_appointments", "appointments_week", "allergies"],
};
const defaultLayoutFor = (role) => ROLE_DEFAULTS[role] || DEFAULT_LAYOUT;
const DEF_BY_KEY = Object.fromEntries(REGISTRY.map((r) => [r.key, r]));

function CustomizeModal({ open, onClose, layout, setAndSave, roleDefault }) {
  const toggle = (key) => {
    const next = layout.includes(key) ? layout.filter((k) => k !== key) : [...layout, key];
    setAndSave(next);
  };
  return (
    <Modal open={open} onClose={onClose} title="Customize Dashboard" wide>
      <p className="mb-4 text-xs text-[#7B6157]">Choose which widgets appear on your dashboard. Drag any widget on the dashboard to reorder. Your layout is saved to your profile.</p>
      {GROUP_ORDER.map((g) => (
        <div key={g} className="mb-4">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{g}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {REGISTRY.filter((r) => r.group === g).map((r) => {
              const on = layout.includes(r.key);
              return (
                <button key={r.key} data-testid={`widget-toggle-${r.key}`} onClick={() => toggle(r.key)} aria-pressed={on}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-medium transition-colors ${on ? "border-[#4E342E] bg-[#4E342E] text-white" : "border-[#EFEBE0] bg-[#FDFCF8] text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>
                  <r.icon size={14} className="shrink-0" /> <span className="truncate">{r.title}</span>
                  <span className={`ml-auto text-[10px] font-bold ${on ? "text-[#FFB300]" : "text-[#84675B]"}`}>{on ? "ON" : "OFF"}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex justify-between border-t border-[#F1EEE7] pt-4">
        <Btn variant="outline" data-testid="widget-reset-btn" onClick={() => setAndSave(roleDefault)}><RotateCcw size={13} /> Reset to default</Btn>
        <Btn data-testid="widget-done-btn" onClick={onClose}>Done</Btn>
      </div>
    </Modal>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [wdata, setWdata] = useState(null);
  const [layout, setLayout] = useState(null);
  const [customize, setCustomize] = useState(false);
  const layoutRef = useRef(null);
  const dragKey = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reqBadge, setReqBadge] = useState(0);
  const [signBadge, setSignBadge] = useState(0);
  useEffect(() => {
    api.get("/staff-requests/counts").then((r) => setReqBadge(r.data.badge || 0)).catch(() => {});
    api.post("/overdue-digest/run").catch(() => {});
    if (user?.role === "admin" || user?.role === "clinician" || user?.is_super_admin) {
      api.get("/orders/sign-off-queue").then((r) => setSignBadge(r.data.count || 0)).catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/dashboard/widgets").then((r) => setWdata(r.data)).catch(() => {});
    api.get("/me/dashboard-layout").then((r) => {
      const saved = r.data.widgets;
      let valid = Array.isArray(saved) ? saved.filter((k) => DEF_BY_KEY[k]) : null;
      if (valid && valid.length && !valid.includes("mini_calendar") && !localStorage.getItem("cocoa_minical_v1")) {
        valid = [...valid, "mini_calendar"];
        localStorage.setItem("cocoa_minical_v1", "1");
        api.put("/me/dashboard-layout", { widgets: valid }).catch(() => {});
      }
      setLayout(valid && valid.length ? valid : defaultLayoutFor(user?.role));
    }).catch(() => setLayout(defaultLayoutFor(user?.role)));
  }, []);
  useEffect(() => { layoutRef.current = layout; }, [layout]);

  const persist = (next) => api.put("/me/dashboard-layout", { widgets: next }).catch(() => {});
  const setAndSave = (next) => { setLayout(next); persist(next); };

  const onDragStart = (k) => () => { dragKey.current = k; };
  const onDragOver = (k) => (e) => {
    e.preventDefault();
    const from = dragKey.current;
    if (!from || from === k) return;
    setLayout((l) => {
      const next = l.filter((x) => x !== from);
      next.splice(next.indexOf(k), 0, from);
      return next;
    });
  };
  const onDragEnd = () => { dragKey.current = null; persist(layoutRef.current); };

  const actRequest = async (id, status) => {
    await api.put(`/appointment-requests/${id}`, { status });
    toast.success(`Request ${status}`);
    load();
  };
  const toClient = (id) => id && navigate(`/clients/${id}/console`);
  const ctx = { navigate, toClient, actRequest, load, photos: wdata?.client_photos || {} };
  const [tasksModal, setTasksModal] = useState(false);
  const [showAll, setShowAll] = useState(null);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const { compact, toggle: toggleDensity } = useDensity();

  if (!data || !layout) return <div className="py-20 text-center text-[#84675B]">Loading dashboard…</div>;
  const d = data;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  const stats = [
    { icon: Users, label: "Active clients", value: d.stats.active_clients, tone: "from-[#4E342E] to-[#6D4C41]", to: "/clients" },
    { icon: CalendarDays, label: "Appointments today", value: d.stats.appointments_today, tone: "from-[#8D6E63] to-[#A1887F]", to: "/scheduler" },
    { icon: CalendarClock, label: "Appointment requests", value: d.appointment_requests.length, tone: "from-[#00695C] to-[#26A69A]", action: () => setShowAll("appointment_requests") },
    { icon: CheckSquare, label: "Open tasks", value: d.stats.open_tasks, tone: "from-[#B26A00] to-[#E65100]", action: () => setTasksModal(true) },
    { icon: PenLine, label: "Awaiting signature", value: d.stats.docs_awaiting, tone: "from-[#5E35B1] to-[#7E57C2]", to: "/clinical/progress-notes" },
  ];

  const renderSpecial = (key) => {
    if (key === "stat_cards") return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {stats.map((s, i) => (
          <button type="button" key={s.label} onClick={() => (s.action ? s.action() : navigate(s.to))}
            className={`stat-3d relative overflow-hidden rounded-2xl bg-gradient-to-br ${s.tone} p-5 text-white text-left cursor-pointer transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#FFB300]/60`}
            data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <s.icon size={54} className="absolute -right-2 -bottom-2 opacity-15" />
            <div className="font-heading text-4xl font-bold"><CountUp value={s.value} /></div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-white/75">{s.label}</div>
          </button>
        ))}
      </div>
    );
    return (
      <Card className="p-5" data-testid="action-center">
        <div className="mb-3 flex items-center gap-2">
          <div className="h-8 w-8 rounded-xl bg-[#3E2723] flex items-center justify-center text-[#FFB300]"><Brain size={16} /></div>
          <h3 className="font-heading text-sm font-bold text-[#3E2723]">Action Center - what needs your attention</h3>
          <InfoTip text="Items are ranked by severity across every module: critical risks first, then warnings, then informational nudges. Click any item to jump straight to where you can act on it." />
          <Zap size={14} className="text-[#FFB300]" />
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          {d.action_center.map((a, i) => (
            <button key={i} data-testid={`action-item-${i}`} onClick={() => navigate(a.link)}
              className={`flex items-start justify-between gap-2 rounded-xl px-4 py-3 text-left transition-transform hover:scale-[1.01] ${SEV_STYLE[a.severity]}`}>
              <div className="min-w-0">
                <div className="text-sm font-bold text-[#3E2723]">{a.title}</div>
                <div className="text-xs text-[#5D4037]">{a.detail}{a.clients?.length > 0 && ` - ${a.clients.join(", ")}${a.clients.length >= 3 ? "…" : ""}`}</div>
              </div>
              <ChevronRight size={16} className="mt-0.5 shrink-0 text-[#7B6157]" />
            </button>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-3xl font-bold text-[#3E2723]" data-testid="dashboard-greeting">
          {greeting}, {user?.first_name} <span className="font-normal text-[#7B6157] text-xl">- it&apos;s {dayjs().format("dddd, MMMM D, YYYY")}</span>
        </h1>
        <div className="flex items-center gap-2">
          <button type="button" onClick={toggleDensity} data-testid="dashboard-density-toggle"
            title={compact ? "Switch to comfortable layout (more spacing)" : "Switch to compact layout (see about 20% more per screen)"}
            aria-pressed={compact}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${compact ? "border-[#4E342E] bg-[#4E342E] text-white hover:bg-[#3E2723]" : "border-[#D7CCC8] bg-white text-[#4E342E] hover:bg-[#F5F2EB]"}`}>
            {compact ? <Maximize2 size={13} /> : <Minimize2 size={13} />}
            {compact ? "Comfortable" : "Compact"}
          </button>
          <Btn variant="outline" data-testid="dashboard-customize-btn" onClick={() => setCustomize(true)}>
            <SlidersHorizontal size={14} /> Customize
          </Btn>
        </div>
      </div>

      <button type="button" data-testid="dashboard-start-new-client-hero" onClick={() => setNewClientOpen(true)}
        className="mb-5 flex w-full items-center justify-between gap-4 rounded-2xl border border-[#4E342E]/15 bg-gradient-to-r from-[#4E342E] via-[#5D4037] to-[#8D6E63] px-5 py-4 text-left text-white shadow-md transition-transform hover:-translate-y-0.5 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#FFB300]/60">
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/15 text-[#FFB300]">
            <UserPlus size={22} />
          </div>
          <div>
            <div className="font-heading text-base font-bold">Start a new client</div>
            <div className="mt-0.5 text-[11px] text-white/75">Quick chart in under 30 seconds. Full Intake and signed consents can follow.</div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide">
          <Sparkles size={12} className="text-[#FFB300]" /> New chart
        </div>
      </button>

      <QuickClientModal open={newClientOpen} onClose={() => setNewClientOpen(false)}
        onCreated={(cl) => { setNewClientOpen(false); navigate(`/clients/${cl.id}/console`); }} />

      <div className="mb-5 flex flex-wrap gap-2" data-testid="dashboard-quick-buttons">
        {[["All Clients", Users, "/clients/all"], ["Tasks", CheckSquare, "/tasks"], ["Groups", UsersRound, "/groups"],
          ...(user?.role === "admin" || user?.is_super_admin ? [["User Management", UserCog, "/users"]] : []),
          ["Requests", Inbox, "/requests"],
          ...(user?.role === "admin" || user?.role === "clinician" || user?.is_super_admin ? [["Order Sign-Off", PenLine, "/orders/sign-off"]] : [])].map(([label, Icon, to]) => (
          <button key={to} data-testid={`quick-btn-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => navigate(to)}
            className="flex items-center gap-2 rounded-full border border-[#EFEBE0] bg-white px-4 py-2 text-sm font-bold text-[#4E342E] shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#8D6E63] hover:shadow-md">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#F5F2EB] text-[#7B6157]"><Icon size={13} /></span>{label}
            {to === "/requests" && reqBadge > 0 && <span data-testid="quick-btn-requests-badge" className="rounded-full bg-[#C62828] px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">{reqBadge}</span>}
            {to === "/orders/sign-off" && signBadge > 0 && <span data-testid="quick-btn-signoff-badge" className="rounded-full bg-[#C62828] px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">{signBadge}</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 auto-rows-min">
        {layout.map((key) => {
          const def = DEF_BY_KEY[key];
          if (!def) return null;
          const span = def.span === "full" ? "md:col-span-2 xl:col-span-3" : def.span === 2 ? "md:col-span-2" : "";
          const dragProps = {
            draggable: true, onDragStart: onDragStart(key), onDragOver: onDragOver(key), onDragEnd,
          };
          if (def.key === "stat_cards" || def.key === "action_center") {
            return (
              <div key={key} {...dragProps} className={`group relative cursor-grab active:cursor-grabbing ${span}`} data-testid={`dash-widget-${key}`}>
                <GripVertical size={14} className="absolute -left-1 top-2 z-10 text-[#84675B] opacity-0 group-hover:opacity-100 transition-opacity" />
                {renderSpecial(key)}
              </div>
            );
          }
          const count = def.count ? def.count(d, wdata) : undefined;
          const cardClick = def.link ? (e) => { if (!e.target.closest("button,a,input,textarea,select,[role=button]")) navigate(def.link); } : undefined;
          return (
            <div key={key} {...dragProps} className={`group cursor-grab active:cursor-grabbing ${span}`} data-testid={`dash-widget-${key}`}>
              <Card onClick={cardClick} className={`dash-card p-5 flex flex-col h-full ${def.span === "full" ? "" : "min-h-[320px]"} ${def.link ? "cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all" : ""}`}>
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <GripVertical size={14} className="shrink-0 text-[#D7CCC8] opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="h-8 w-8 shrink-0 rounded-xl bg-[#F5F2EB] flex items-center justify-center text-[#7B6157]"><def.icon size={16} /></div>
                    {def.link ? (
                      <button type="button" data-testid={`widget-link-${key}`} onClick={() => navigate(def.link)}
                        className="flex items-center gap-1 min-w-0 group/link" title={`Open ${def.title}`}>
                        <h3 className="font-heading text-sm font-bold text-[#3E2723] truncate group-hover/link:underline">{def.title}</h3>
                        <ChevronRight size={13} className="shrink-0 text-[#84675B] group-hover/link:text-[#4E342E]" />
                      </button>
                    ) : (
                      <h3 className="font-heading text-sm font-bold text-[#3E2723] truncate">{def.title}</h3>
                    )}
                  </div>
                  {count !== undefined && <span className="ml-2 shrink-0 rounded-full bg-[#4E342E] text-white text-xs font-bold px-2.5 py-0.5">{count}</span>}
                </div>
                <div className="flex-1 min-h-0">{def.render(d, wdata, ctx)}</div>
                {count > 5 && !def.noShowAll && (
                  <button type="button" data-testid={`widget-showall-${key}`}
                    onClick={(e) => { e.stopPropagation(); setShowAll(key); }}
                    className="mt-2 w-full rounded-lg border border-dashed border-[#D7CCC8] py-1.5 text-xs font-bold text-[#7B6157] transition-colors hover:bg-[#F5F2EB] hover:text-[#4E342E]">
                    Show all {count}
                  </button>
                )}
              </Card>
            </div>
          );
        })}
      </div>

      <CustomizeModal open={customize} onClose={() => setCustomize(false)} layout={layout} setAndSave={setAndSave} roleDefault={defaultLayoutFor(user?.role)} />

      {showAll && DEF_BY_KEY[showAll] && (
        <Modal open onClose={() => setShowAll(null)} title={`${DEF_BY_KEY[showAll].title}${DEF_BY_KEY[showAll].count ? ` (${DEF_BY_KEY[showAll].count(d, wdata)})` : ""}`} wide>
          <div className="max-h-[60vh] overflow-y-auto -mx-1 px-1" data-testid="widget-showall-modal">
            {DEF_BY_KEY[showAll].render(d, wdata, { ...ctx, all: true })}
          </div>
          <div className="mt-4 flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
            <Btn variant="ghost" onClick={() => setShowAll(null)}>Close</Btn>
            {DEF_BY_KEY[showAll].link && (
              <Btn data-testid="widget-showall-open" onClick={() => { setShowAll(null); navigate(DEF_BY_KEY[showAll].link); }}>
                Open {DEF_BY_KEY[showAll].title}
              </Btn>
            )}
          </div>
        </Modal>
      )}

      <Modal open={tasksModal} onClose={() => setTasksModal(false)} title={`Open Tasks (${d.stats.open_tasks})`} wide>
        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1" data-testid="tasks-modal-list">
          {d.client_tasks.length === 0 ? <Empty text="No open tasks" /> : d.client_tasks.map((t) => (
            <button key={t.id} type="button" data-testid={`tasks-modal-row-${t.id}`}
              onClick={() => { setTasksModal(false); navigate(`/tasks?open=${t.id}`); }}
              className="flex w-full items-center justify-between gap-3 border-b border-[#EFEBE0] last:border-0 py-2.5 px-1 text-left rounded-lg hover:bg-[#FDFCF8] transition-colors group/task">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-[#3E2723] group-hover/task:underline">{t.title}</div>
                <div className="text-xs text-[#84675B]">{t.client_name}{t.due_date ? ` · due ${dayjs(t.due_date).format("MM/DD/YYYY")}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge value={t.priority} />
                {!t.signed && <span className="rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[10px] font-bold text-[#B84A00]">UNSIGNED</span>}
                <ChevronRight size={14} className="text-[#D7CCC8] group-hover/task:text-[#4E342E]" />
              </div>
            </button>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2 border-t border-[#F1EEE7] pt-4">
          <Btn variant="ghost" onClick={() => setTasksModal(false)}>Close</Btn>
          <Btn data-testid="tasks-modal-goto-btn" onClick={() => { setTasksModal(false); navigate("/tasks"); }}>
            <ListChecks size={14} /> Open Tasks Page
          </Btn>
        </div>
      </Modal>
    </div>
  );
}
